// The SDK is built with `emitDecoratorMetadata`, so its DTO classes call
// `Reflect.metadata(...)` at module evaluation. Server entries already pull the
// polyfill in (every route.ts imports it first); the browser has no such entry,
// and this is the first module in the client graph to reach the SDK — without
// the import below the whole client bundle dies on `Reflect.getMetadata is not
// a function` and the page renders server-side but never hydrates.
import 'reflect-metadata';
import {
    Language,
    PriceRuleFlag,
    RestApiDeliveryMethodDto,
    RestApiPaymentMethodDto,
    RestApiPriceRuleDto,
    RestApiPriceValueDto,
    RestApiVenueDto,
} from '@order-mage/integration-sdk';
import { resolveEffectiveAddons } from '@/entities/cart/lib/addons';
import {
    createGetItemFinalPrice,
    createGetItemPrices,
    getPriceForItem,
    type MenuListsData,
} from '@/entities/cart/lib/item-price';
import { matchAreaInMethod, resolveDeliveryZoneTime } from '@/entities/cart/lib/match-delivery-area';
import { collectAddonMemberPackages, buildPackageItemByIdMap } from '@/entities/cart/lib/packages';
import { getApplicableTips, resolveTipAmount } from '@/entities/cart/lib/tips';
import type { CartFormValues } from '@/entities/cart/model/cart-context';
import {
    resolvePaymentMethodFeePrice,
    type FeeTaxedPrice,
    type ResolvedFeePrice,
} from '@/shared/lib/payment-fee';
import { getPaymentType, roundPrice, roundTip } from '@/shared/lib/priceRound';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';

type PaymentMethodWithTaxedPrice = RestApiPaymentMethodDto & {
    taxedPrice?: FeeTaxedPrice;
};

export type CartTotalsInput = {
    form: Pick<
        CartFormValues,
        'items' | 'deliveryMethod' | 'paymentMethod' | 'time' | 'tips' | 'topUpOption'
    >;
    menuLists: MenuListsData | null | undefined;
    deliveryMethods: RestApiDeliveryMethodDto[] | null | undefined;
    paymentMethods: RestApiPaymentMethodDto[] | null | undefined;
    venue: RestApiVenueDto | null | undefined;
    selectedCurrency: Currency;
    userLanguage: Language;
    ecoDiscount?: { hasDiscount: boolean } | null;
};

export type CartTotalsSnapshot = {
    deliveryMethod: RestApiDeliveryMethodDto | null;
    matchedArea: NonNullable<RestApiDeliveryMethodDto['areas']>[number] | null;
    areaPrice: RestApiPriceValueDto | null;
    deliveryPrice: { total: number; base: number; area: number };
    subtotal: number;
    itemsSum: number;
    packagesSum: number;
    /** Rounded tip amount per BE tip id — only tips offered by the current payment + delivery method. */
    tips: Record<string, number>;
    tipsTotal: number;
    paymentMethodFee: number;
    minPrice: RestApiPriceValueDto | null;
    topUpFee: number;
    total: number;
    rounding: number;
    roundedTotal: number;
    getItemPrices: ReturnType<typeof createGetItemPrices>;
    getItemFinalPrice: ReturnType<typeof createGetItemFinalPrice>;
    getPaymentMethodPrice: (idOrMethod: string | PaymentMethodWithTaxedPrice) => ResolvedFeePrice | null;
    getDeliveryMethodArea: (
        method: RestApiDeliveryMethodDto,
        lat: number,
        lon: number,
    ) => ReturnType<typeof matchAreaInMethod>;
    getDeliveryMethodAreaPrice: (
        method: RestApiDeliveryMethodDto,
        lat: number,
        lon: number,
    ) => RestApiPriceValueDto | null;
    getMinOrderRestriction: (method: { priceRules?: RestApiPriceRuleDto[] | null }) => {
        minPrice: RestApiPriceValueDto | null;
        isBelowMinOrder: boolean;
        allowsTopUp: boolean;
        isDisabled: boolean;
    };
};

const emptyFns = {
    getItemPrices: (() => null) as ReturnType<typeof createGetItemPrices>,
    getItemFinalPrice: (() => null) as ReturnType<typeof createGetItemFinalPrice>,
    getPaymentMethodPrice: () => null,
    getDeliveryMethodArea: () => null,
    getDeliveryMethodAreaPrice: () => null,
    getMinOrderRestriction: () => ({
        minPrice: null,
        isBelowMinOrder: false,
        allowsTopUp: false,
        isDisabled: false,
    }),
};

export const EMPTY_CART_TOTALS: CartTotalsSnapshot = {
    deliveryMethod: null,
    matchedArea: null,
    areaPrice: null,
    deliveryPrice: { total: 0, base: 0, area: 0 },
    subtotal: 0,
    itemsSum: 0,
    packagesSum: 0,
    tips: {},
    tipsTotal: 0,
    paymentMethodFee: 0,
    minPrice: null,
    topUpFee: 0,
    total: 0,
    rounding: 0,
    roundedTotal: 0,
    ...emptyFns,
};

/**
 * Single source of cart pricing math. UI must only read the snapshot — never reimplement.
 */
export function computeCartTotals(input: CartTotalsInput): CartTotalsSnapshot {
    const {
        form,
        menuLists,
        deliveryMethods,
        paymentMethods,
        venue,
        selectedCurrency,
        userLanguage,
        ecoDiscount,
    } = input;

    const venueCountry = (venue?.address?.country as Country) ?? Country.CZ;
    const deliveryMethod = deliveryMethods?.find((m) => m.id === form.deliveryMethod.id) ?? null;
    const deliveryMethodDefinitionId = deliveryMethod?.deliveryMethodDefinitionId;

    const lat = parseFloat(form.deliveryMethod.address?.lat ?? '');
    const lon = parseFloat(form.deliveryMethod.address?.lon ?? '');
    const zoneTime = resolveDeliveryZoneTime(form.time);
    const matchedArea =
        deliveryMethod && Number.isFinite(lat) && Number.isFinite(lon)
            ? matchAreaInMethod(deliveryMethod, { lat, lon }, zoneTime)
            : null;

    const packageItemById = buildPackageItemByIdMap(menuLists?.menuLists);
    const getItemPrices = createGetItemPrices({
        menuLists,
        selectedCountry: venueCountry,
        selectedCurrency,
        deliveryMethodDefinitionId,
        userLanguage,
        packageItemById,
    });
    const getItemFinalPrice = createGetItemFinalPrice(menuLists, getItemPrices);

    const getDeliveryMethodArea = (method: RestApiDeliveryMethodDto, a: number, b: number) => {
        if (!a || !b) return null;
        return matchAreaInMethod(method, { lat: a, lon: b }, zoneTime);
    };

    let subtotal = 0;
    let packagesSum = 0;

    for (const field of form.items) {
        const variantHash = field.variant || field.hash;
        // packages + (cached) item price
        const prices = getItemPrices(variantHash);
        const parentAddons = menuLists?.items?.[field.hash]?.item?.addons;
        const finalPrice = getItemFinalPrice(
            { variant: field.variant, addons: field.addons, memberVariants: field.memberVariants },
            field.hash,
            parentAddons,
        );

        const amount = finalPrice?.amount || prices?.itemPrice?.amount;
        if (amount) {
            subtotal += parseFloat(amount) * field.quantity;
        }

        const itemPackagesSum = (prices?.packagePrices ?? []).reduce(
            (sum, pkg) => sum + parseFloat(pkg.priceValue.amount),
            0,
        );
        packagesSum += itemPackagesSum * field.quantity;

        const addonMemberPackages = collectAddonMemberPackages(
            field.addons,
            menuLists ?? undefined,
            deliveryMethodDefinitionId,
            userLanguage,
            packageItemById,
            {
                addons: menuLists?.items
                    ? resolveEffectiveAddons(menuLists.items, parentAddons, field.variant, field.hash)
                    : undefined,
                memberVariants: field.memberVariants,
            },
        );
        for (const pkg of addonMemberPackages) {
            if (!pkg.packageItem) continue;
            const priceValue = getPriceForItem(pkg.packageItem, venueCountry, selectedCurrency);
            if (!priceValue?.amount) continue;
            packagesSum += parseFloat(priceValue.amount) * pkg.addonQuantity * field.quantity;
        }
    }

    // A PERCENTAGE fee is a share of items + packages — so this can only be resolved
    // once the items loop above has run. Delivery, tips and the min-order top-up are
    // deliberately not part of the base.
    const paymentFeeBase = subtotal + packagesSum;

    const getPaymentMethodPrice = (idOrMethod: string | PaymentMethodWithTaxedPrice) => {
        const paymentMethod =
            typeof idOrMethod === 'string'
                ? (paymentMethods?.find((pm) => pm.id === idOrMethod) as
                      | PaymentMethodWithTaxedPrice
                      | undefined)
                : idOrMethod;
        if (!paymentMethod) return null;
        return resolvePaymentMethodFeePrice({
            taxedPrice: paymentMethod.taxedPrice,
            base: paymentFeeBase,
            selectedCurrency,
            selectedCountry: venueCountry,
        });
    };

    const resolveAreaPrice = (
        area: NonNullable<RestApiDeliveryMethodDto['areas']>[number] | null | undefined,
        currentSubtotal: number,
    ): RestApiPriceValueDto | null => {
        if (!area?.deliveryAvailable) return null;
        const areaPrice = getPriceForItem(
            { prices: area.taxedPrice ? [area.taxedPrice] : [] },
            venueCountry,
            selectedCurrency,
        );
        const discountRule = area.priceRules?.find((priceRule) => priceRule.type === 'DISCOUNT_THRESHOLD');
        const discountPriceAbove = discountRule
            ? getPriceForItem(
                  { prices: [{ priceGroup: discountRule.priceGroup }] },
                  venueCountry,
                  selectedCurrency,
              )
            : null;
        if (
            areaPrice &&
            discountRule &&
            discountPriceAbove?.amount &&
            currentSubtotal >= parseFloat(discountPriceAbove.amount)
        ) {
            const discountPercentage = parseFloat(discountRule.params?.discount ?? '0');
            return {
                ...areaPrice,
                amount: String(parseFloat(areaPrice.amount) * (1 - discountPercentage / 100)),
            };
        }
        return areaPrice;
    };

    const areaPrice = resolveAreaPrice(matchedArea, subtotal);

    const getDeliveryMethodAreaPrice = (
        method: RestApiDeliveryMethodDto,
        a: number,
        b: number,
    ): RestApiPriceValueDto | null => {
        if (!a || !b || !method.areas?.length) return null;
        return resolveAreaPrice(getDeliveryMethodArea(method, a, b), subtotal);
    };

    let deliveryPrice = { total: 0, base: 0, area: 0 };
    if (
        !(
            deliveryMethod?.areas &&
            deliveryMethod.areas.length > 0 &&
            (!matchedArea || !matchedArea.deliveryAvailable)
        )
    ) {
        const deliveryMethodPrice = deliveryMethod?.taxedPrice
            ? getPriceForItem({ prices: [deliveryMethod.taxedPrice] }, venueCountry, selectedCurrency)
            : { amount: '0' };
        const rawBaseAmount = parseFloat(deliveryMethodPrice?.amount || '0');
        const rawAreaAmount = parseFloat(areaPrice?.amount ?? '0');
        const rawTotal = rawBaseAmount + rawAreaAmount;
        const ecoDiscountPercentage = deliveryMethod?.extensionTimedOrders?.ecoDiscount.percentage ?? 0;
        const finalTotal = ecoDiscount?.hasDiscount ? rawTotal * (1 - ecoDiscountPercentage / 100) : rawTotal;
        const discountFactor = rawTotal > 0 ? finalTotal / rawTotal : 1;
        deliveryPrice = {
            total: finalTotal,
            base: Math.round(rawBaseAmount * discountFactor * 100) / 100,
            area: Math.round(rawAreaAmount * discountFactor * 100) / 100,
        };
    }

    let paymentMethodFee = 0;
    if (form.paymentMethod.id) {
        const fee = getPaymentMethodPrice(form.paymentMethod.id);
        if (fee?.amount) paymentMethodFee = parseFloat(fee.amount);
    }

    const areaRule = matchedArea?.deliveryAvailable
        ? matchedArea.priceRules?.find((priceRule) => priceRule.type === 'MIN_RESTRICTION')
        : undefined;
    const areaMinPrice = areaRule
        ? getPriceForItem({ prices: [{ priceGroup: areaRule.priceGroup }] }, venueCountry, selectedCurrency)
        : null;
    const areaOverridesParent = areaRule?.flags?.includes(PriceRuleFlag.OVERRIDE_PARENT_SETUP) ?? false;
    const methodRule = !areaOverridesParent
        ? deliveryMethod?.priceRules?.find((rule) => rule.type === 'MIN_RESTRICTION')
        : undefined;
    const methodMinPrice = methodRule
        ? getPriceForItem({ prices: [{ priceGroup: methodRule.priceGroup }] }, venueCountry, selectedCurrency)
        : null;
    const paymentMethod = form.paymentMethod.id
        ? paymentMethods?.find((pm) => pm.id === form.paymentMethod.id)
        : null;
    // Tips are offered per payment method (and may restrict themselves to delivery
    // methods), so an unofferable tip left over in the form must never reach the total.
    const tips: Record<string, number> = {};
    for (const tip of getApplicableTips(paymentMethod, form.deliveryMethod.id)) {
        const amount = roundTip(
            resolveTipAmount(form.tips?.[tip.id], subtotal),
            selectedCurrency,
            venueCountry,
        );
        if (amount > 0) tips[tip.id] = amount;
    }
    const tipsTotal = Object.values(tips).reduce((sum, amount) => sum + amount, 0);

    const paymentRule = paymentMethod?.priceRules?.find((rule) => rule.type === 'MIN_RESTRICTION');
    const paymentMinPrice = paymentRule
        ? getPriceForItem(
              { prices: [{ priceGroup: paymentRule.priceGroup }] },
              venueCountry,
              selectedCurrency,
          )
        : null;
    const candidates = [areaMinPrice, methodMinPrice, paymentMinPrice].filter(
        (p): p is RestApiPriceValueDto => p !== null,
    );
    const minPrice =
        candidates.length === 0
            ? null
            : candidates.reduce((max, curr) =>
                  parseFloat(String(curr.amount)) > parseFloat(String(max.amount)) ? curr : max,
              );

    const topUpFee = !minPrice ? 0 : Math.max(parseFloat(minPrice.amount) - subtotal, 0);

    const total = subtotal + tipsTotal + paymentMethodFee + deliveryPrice.total + packagesSum + topUpFee;

    const selectedPaymentMethod = form.paymentMethod.id
        ? paymentMethods?.find((pm) => pm.id === form.paymentMethod.id)
        : undefined;
    const paymentType = getPaymentType(selectedPaymentMethod);
    const roundedTotal = roundPrice(total, selectedCurrency, venueCountry, paymentType);
    const rounding = Math.round((roundedTotal - total) * 10000) / 10000;

    const getMinOrderRestriction = (method: { priceRules?: RestApiPriceRuleDto[] | null }) => {
        const minRestrictionRule = method.priceRules?.find((rule) => rule.type === 'MIN_RESTRICTION');
        const methodMin = minRestrictionRule
            ? getPriceForItem({ prices: [minRestrictionRule] }, venueCountry, selectedCurrency)
            : null;
        const allowsTopUp = !!minRestrictionRule?.flags?.includes(PriceRuleFlag.ALLOW_PRICE_TOP_UP);
        const isBelowMinOrder = !!methodMin && subtotal < parseFloat(methodMin.amount);
        return {
            minPrice: methodMin,
            isBelowMinOrder,
            allowsTopUp,
            isDisabled: isBelowMinOrder && !allowsTopUp,
        };
    };

    return {
        deliveryMethod,
        matchedArea,
        areaPrice,
        deliveryPrice,
        subtotal,
        itemsSum: subtotal,
        packagesSum,
        tips,
        tipsTotal,
        paymentMethodFee,
        minPrice,
        topUpFee,
        total,
        rounding,
        roundedTotal,
        getItemPrices,
        getItemFinalPrice,
        getPaymentMethodPrice,
        getDeliveryMethodArea,
        getDeliveryMethodAreaPrice,
        getMinOrderRestriction,
    };
}
